import React, { useEffect, useState } from "react";
import { api, rupiah } from "@/lib/api";
import { PageHeader, Empty } from "@/components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { BookOpen, Wallet, PiggyBank, ArrowDownCircle, ArrowUpCircle, Loader2, FileText, FileSpreadsheet } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "medium", timeStyle: "short" }) : "-");

export default function ParentLedger() {
  const [data, setData] = useState({ rows: [], balances: [] });
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/parent/ledger").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const dl = async (format) => {
    try {
      const r = await api.get(`/parent/ledger-export?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `buku_tabungan.${format === "xlsx" ? "xlsx" : "pdf"}`; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const rows = data.rows.filter((r) => cat === "all" || r.category === cat);

  return (
    <div>
      <PageHeader title="History Uang Jajan & Tabungan" subtitle="Seperti buku tabungan: catatan uang jajan & tabungan masuk dan keluar beserta sumbernya."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => dl("pdf")} data-testid="parent-ledger-pdf" className="rounded-xl"><FileText size={16} className="mr-1.5" /> PDF</Button>
            <Button variant="outline" onClick={() => dl("xlsx")} data-testid="parent-ledger-xlsx" className="rounded-xl"><FileSpreadsheet size={16} className="mr-1.5" /> Excel</Button>
          </div>
        }
      />

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {data.balances.map((s) => (
          <div key={s.id} className="bg-white border border-stone-200 rounded-2xl p-4 shadow-sm">
            <p className="font-heading font-semibold text-stone-900">{s.name}</p>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="rounded-xl bg-stone-50 border p-3">
                <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><Wallet size={12} /> Uang Jajan</p>
                <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.saldo)}</p>
              </div>
              <div className="rounded-xl bg-stone-50 border p-3">
                <p className="text-[11px] uppercase tracking-wider text-stone-400 flex items-center gap-1"><PiggyBank size={12} /> Tabungan</p>
                <p className="font-heading font-bold text-stone-900 mt-1">{rupiah(s.tabungan)}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Tabs value={cat} onValueChange={setCat} className="mb-4">
        <TabsList>
          <TabsTrigger value="all" data-testid="ledger-tab-all">Semua</TabsTrigger>
          <TabsTrigger value="saldo" data-testid="ledger-tab-saldo">Uang Jajan</TabsTrigger>
          <TabsTrigger value="tabungan" data-testid="ledger-tab-tabungan">Tabungan</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="bg-white border border-stone-200 rounded-2xl shadow-sm overflow-x-auto">
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
        ) : rows.length === 0 ? (
          <Empty><BookOpen className="mx-auto mb-2" /> Belum ada mutasi.</Empty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Tanggal & Jam</TableHead>
                <TableHead>Santri</TableHead>
                <TableHead>Keterangan</TableHead>
                <TableHead>Jenis</TableHead>
                <TableHead className="text-right">Masuk</TableHead>
                <TableHead className="text-right">Keluar</TableHead>
                <TableHead>Petugas</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => (
                <TableRow key={i} data-testid={`ledger-row-${i}`}>
                  <TableCell className="text-sm whitespace-nowrap">{fmt(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.student}</TableCell>
                  <TableCell className="text-sm">{r.note}</TableCell>
                  <TableCell>
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${r.category === "tabungan" ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>
                      {r.category === "tabungan" ? "Tabungan" : "Uang Jajan"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-emerald-600">
                    {r.direction === "in" ? <span className="inline-flex items-center gap-1 justify-end"><ArrowDownCircle size={13} /> {rupiah(r.amount)}</span> : "-"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-rose-600">
                    {r.direction === "out" ? <span className="inline-flex items-center gap-1 justify-end"><ArrowUpCircle size={13} /> {rupiah(r.amount)}</span> : "-"}
                  </TableCell>
                  <TableCell className="text-sm text-stone-500">{r.source}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
